(() => {
  if (window.DeebAccessoryGalleryCounter) return;

  const ROOT_SELECTOR = 'product-info[data-product-family="accessory"]';
  const SLIDE_SELECTOR = '.product__media-list > li[data-media-id]';
  const TOTAL_SELECTOR = '.slider-counter--total';
  const CURRENT_SELECTOR = '.slider-counter--current';

  class AccessoryGalleryCounter {
    constructor(root) {
      this.root = root;
      this.mediaList = root.querySelector('.product__media-list');
      this.total = root.querySelector(TOTAL_SELECTOR);
      this.current = root.querySelector(CURRENT_SELECTOR);
      this.frame = null;
      this.observer = null;
      this.variantUnsubscriber = null;

      if (!this.ready) return;

      this.observer = new MutationObserver(this.scheduleSync);
      this.observer.observe(this.mediaList, { childList: true });
      this.observer.observe(this.total, {
        childList: true,
        characterData: true,
        subtree: true,
      });

      window.addEventListener('pageshow', this.handlePageShow);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange
        );
      }

      this.sync();
    }

    get ready() {
      return Boolean(this.root && this.mediaList && this.total);
    }

    handlePageShow = () => {
      this.scheduleSync();
    };

    handleVariantChange = ({ data } = {}) => {
      if (data?.sectionId && data.sectionId !== this.root.dataset.section) return;
      this.scheduleSync();
    };

    scheduleSync = () => {
      if (!this.ready || this.frame !== null) return;

      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.sync();
      });
    };

    sync() {
      if (!this.ready) return 0;

      const count = this.root.querySelectorAll(SLIDE_SELECTOR).length;
      const nextTotal = String(count);

      if (this.total.textContent !== nextTotal) {
        this.total.textContent = nextTotal;
      }

      if (this.root.dataset.deebAccessoryMediaCount !== nextTotal) {
        this.root.dataset.deebAccessoryMediaCount = nextTotal;
      }

      if (this.current) {
        const currentValue = Number.parseInt(this.current.textContent, 10);

        if (!Number.isFinite(currentValue) || currentValue < 1) {
          this.current.textContent = count > 0 ? '1' : '0';
        } else if (currentValue > count) {
          this.current.textContent = String(count);
        }
      }

      return count;
    }

    destroy() {
      this.observer?.disconnect();
      this.variantUnsubscriber?.();
      window.removeEventListener('pageshow', this.handlePageShow);

      if (this.frame !== null) {
        window.cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }
  }

  const instances = new WeakMap();
  const activeInstances = new Set();

  const collectRoots = (scope = document) => {
    const roots = [];

    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) {
      roots.push(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    return roots;
  };

  const initializeRoot = (root) => {
    if (!(root instanceof HTMLElement) || !root.matches(ROOT_SELECTOR)) return null;

    const existing = instances.get(root);
    if (existing) return existing;

    const instance = new AccessoryGalleryCounter(root);
    if (!instance.ready) return null;

    instances.set(root, instance);
    activeInstances.add(instance);
    return instance;
  };

  const initializeAll = (scope = document) => {
    collectRoots(scope).forEach(initializeRoot);
  };

  const destroyAll = (scope) => {
    collectRoots(scope).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;

      instance.destroy();
      activeInstances.delete(instance);
      instances.delete(root);
    });
  };

  document.addEventListener('product-info:loaded', (event) => {
    initializeRoot(event.target);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyAll(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document), {
      once: true,
    });
  } else {
    initializeAll(document);
  }

  window.DeebAccessoryGalleryCounter = Object.freeze({
    sync() {
      activeInstances.forEach((instance) => instance.scheduleSync());
    },

    get activeInstances() {
      return activeInstances.size;
    },
  });
})();
